import { Box, Chip, Typography, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EngineeringIcon from "@mui/icons-material/Engineering";
import BoltIcon from "@mui/icons-material/Bolt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const stageLabelColors = {
  default: { bg: "#e0e0e0", color: "#424242" },
};

function InfoItem({ icon, label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {icon}
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function ProjectHeader({ projectData, onNavigateTab }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const currentStage = projectData?.stagesHistory?.[0]?.stages?.stageName;
  const currentProvidence = projectData?.providencesHistory?.[0]?.providences?.providenceName;
  const isCompleted = currentProvidence?.toLowerCase()?.includes("atividade conclu");
  const engineerName = projectData?.engineers?.name;
  const clientName = projectData?.clients?.name;
  const power = projectData?.power;
  const price = projectData?.price;

  // Situação financeira (usa resumo computado pelo backend)
  const paymentSummary = projectData?.paymentSummary;
  const totalPaid = paymentSummary?.totalPaid || 0;
  const hasOverdue = paymentSummary?.hasOverdue || false;

  const formatPower = (val) => {
    if (!val) return null;
    return `${parseFloat(val).toLocaleString("pt-BR")} kWp`;
  };

  const formatMoney = (val) => {
    if (!val && val !== 0) return null;
    return parseFloat(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}03)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Linha 1: Título + Etapa */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isCompleted && (
            <Tooltip title="Atividade Concluída" arrow>
              <CheckCircleIcon sx={{ fontSize: 28, color: "success.main" }} />
            </Tooltip>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Projeto {projectData.projectNumber} - {projectData.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {currentStage && (
            <Chip
              label={currentStage}
              size="small"
              onClick={() => onNavigateTab?.(3)}
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                cursor: "pointer",
                '&:hover': { opacity: 0.85 },
              }}
            />
          )}
          {currentProvidence && (
            <Chip
              label={currentProvidence}
              size="small"
              variant="outlined"
              onClick={() => onNavigateTab?.(3)}
              sx={{ fontWeight: 500, cursor: "pointer", '&:hover': { opacity: 0.85 } }}
            />
          )}
        </Box>
      </Box>

      {/* Linha 2: Informações resumidas */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isSmallScreen ? 1.5 : 3,
          flexWrap: "wrap",
        }}
      >
        <InfoItem
          icon={<PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
          label="Integrador"
          value={clientName}
        />
        <InfoItem
          icon={<EngineeringIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
          label="Engenheiro"
          value={engineerName}
        />
        <InfoItem
          icon={<BoltIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
          label="Potência"
          value={formatPower(power)}
        />
        {price > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18, color: hasOverdue ? "error.main" : totalPaid >= price ? "success.main" : "text.secondary" }} />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Pago:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                whiteSpace: "nowrap",
                color: hasOverdue ? "error.main" : totalPaid >= price ? "success.main" : "text.primary",
              }}
            >
              {formatMoney(totalPaid)}/{formatMoney(price)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
